/**
 * The panel's status rail.
 *
 * Its job is to make supervision legible: you can see the planner finish, the
 * tutor start writing, and three referees working while you read. Agents that
 * haven't started are dimmed rather than hidden, so the shape of the pipeline
 * is visible from the first moment.
 */

import { AGENTS, type AgentId, type AgentState } from "../../shared/agents.ts";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip.tsx";
import { cn } from "../lib/utils.ts";

interface AgentRailProps {
  readonly agents: Readonly<Record<AgentId, AgentState>>;
  readonly detail: Readonly<Partial<Record<AgentId, string>>>;
}

/**
 * State is carried on an attribute so one class list can describe all five.
 * It is `data-agent` rather than `data-state` because the tooltip trigger
 * wrapping each chip claims `data-state` for its own open/closed state.
 */
const CHIP_STATE_STYLES =
  "border-border bg-muted text-muted-foreground" +
  " data-[agent=pending]:opacity-45" +
  " data-[agent=running]:border-primary data-[agent=running]:bg-brand-soft data-[agent=running]:text-primary data-[agent=running]:animate-pulse motion-reduce:data-[agent=running]:animate-none" +
  " data-[agent=done]:border-border-strong data-[agent=done]:text-secondary-foreground" +
  " data-[agent=skipped]:opacity-50" +
  " data-[agent=failed]:border-warning data-[agent=failed]:text-warning";

/**
 * The rail renders the whole pipeline from the first frame, but the states
 * arrive over SSE — so a roster entry may briefly have no state at all, and an
 * older server may never send one for an agent added since. Treating that as
 * "hasn't started" is both true and the safe default; the alternative is a chip
 * that renders with no styling and reads as ": ." to a screen reader.
 */
const stateOf = (
  agents: Readonly<Record<AgentId, AgentState>>,
  id: AgentId,
): AgentState => agents[id] ?? "pending";

export function AgentRail({ agents, detail }: AgentRailProps) {
  return (
    <ol
      className="mb-4 flex flex-wrap gap-2 font-sans text-xs"
      aria-label="Answer pipeline progress"
    >
      {AGENTS.map((agent) => (
        <Tooltip key={agent.id}>
          <TooltipTrigger asChild>
            <li
              data-agent={stateOf(agents, agent.id)}
              className={cn(
                "flex items-center gap-1 rounded-full border px-2 py-0.5 transition-colors",
                CHIP_STATE_STYLES,
              )}
            >
              <span aria-hidden="true">{agent.glyph}</span>
              <span>{agent.label}</span>
              {detail[agent.id] && (
                <span className="text-muted-foreground italic">
                  {detail[agent.id]}
                </span>
              )}
              {/*
                The duty is repeated here rather than left to the tooltip alone.
                A tooltip on a plain `<li>` only ever opens on hover — the rail
                is a status display, not a control, so there is nothing to tab
                to — which would have made "what does the figures referee
                actually check?" a mouse-only answer. Giving the chip five
                phantom tab stops per streaming message is the worse trade; the
                text costs nothing and reaches everyone.
              */}
              <span className="sr-only">
                : {describe(stateOf(agents, agent.id))}. {agent.duty}
              </span>
            </li>
          </TooltipTrigger>
          <TooltipContent className="max-w-80">{agent.duty}</TooltipContent>
        </Tooltip>
      ))}
    </ol>
  );
}

function describe(state: AgentState): string {
  switch (state) {
    case "pending":
      return "waiting";
    case "running":
      return "working";
    case "done":
      return "finished";
    case "skipped":
      return "not needed";
    case "failed":
      return "unavailable";
  }
}
